import type { useShellController } from "../../controllers/useShellController";
import type { ProjectNavController, ProjectLeaf } from "../../controllers/useProjectNav";
import type { Project } from "../../models/account";
import type { WorkAreaType } from "../../models/eqms";
import logoUrl from "../../assets/logo.jpg";

// Project-centric left rail. 프로젝트 is the main thing here, so the workspace's projects sit at the very
// top as an expandable tree, followed by 채팅 · 워크스페이스 관리(워크스페이스 관리자만) — both workspace-scoped,
// hence below the projects — with 내 초대 / 내 활동 / 내 계정 / 관리자 pinned at the bottom. The two
// platform-wide destinations that belong to no project (앱 · 다운로드) live in the TITLE BAR's top menu.
// Expanding a project reveals the eQMS nav — a flat list of same-level leaves grouped by separators:
// 대시보드·문서함·자료함 / 스프린트·업무유형(QMS·SW·사이버보안·AI) / 검토함·외부문서·관리 — plus 프로젝트 관리(관리자만) and 블로그.

// The four work-area leaves, at the same level as 스프린트 (see confirmed nav structure).
const WORK_AREA_LEAVES: { area: WorkAreaType; label: string }[] = [
  { area: "QUALITY_MANAGEMENT", label: "품질경영(QMS)" },
  { area: "SOFTWARE", label: "소프트웨어(SW)" },
  { area: "CYBERSECURITY", label: "사이버보안" },
  { area: "AI", label: "AI" },
  { area: "CLINICAL", label: "임상" },
];

function leafKey(l: ProjectLeaf): string {
  return l.kind === "tab" ? `tab:${l.tab}` : `eqms:${l.section}:${l.area ?? "-"}`;
}

export function ProjectRail({ nav, c, wsAdmin, onCreateProject }: { nav: ProjectNavController; c: ReturnType<typeof useShellController>; wsAdmin: boolean; onCreateProject: () => void }) {
  const admin = c.admin;
  const active = nav.active;
  const isShell = (v: string) => active.kind === "shell" && active.view === v;
  // Below 900px this rail is an overlay drawer over the content. Anything that navigates has to
  // close it, or the screen the user just chose stays hidden behind it.
  const go = (fn: () => void) => () => { fn(); c.closeNav(); };
  const projects = nav.ws.projects;
  const pendingInvites = nav.ws.myInvitations.filter((i) => i.status === "PENDING").length;

  return (
    // The drawer state rides on the rail itself rather than on an ancestor: one class, one element,
    // nothing for a stacking or containment context in between to interfere with.
    <nav className={`prail ${c.navOpen ? "open" : ""}`} aria-label="프로젝트 탐색">
      <div className="prail-scroll">
        <div className="prail-section">프로젝트</div>
        {nav.ws.loadingWorkspace ? (
          <div className="prail-muted">불러오는 중…</div>
        ) : projects.length === 0 ? (
          <div className="prail-muted">프로젝트가 없습니다</div>
        ) : (
          projects.map((p) => <ProjectNode key={p.id} p={p} nav={nav} admin={wsAdmin} onNavigated={c.closeNav} />)
        )}

        {/* Workspace-scoped, not project-scoped — so they sit under the project tree. */}
        <div className="prail-group-sep" />

        <button
          className={`prail-top ${isShell("chat") ? "on" : ""}`}
          onClick={go(() => nav.showShell("chat"))}
        >
          <span className="prail-ico" aria-hidden>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
          </span> 채팅
        </button>

        {wsAdmin && (
          <button
            className={`prail-top ${isShell("workspace") ? "on" : ""}`}
            disabled={!nav.ws.workspace}
            onClick={go(() => nav.showShell("workspace"))}
          >
            <span className="prail-ico" aria-hidden>◇</span> 워크스페이스 관리
          </button>
        )}
      </div>

      <div className="prail-create">
        <button
          className="prail-create-btn"
          disabled={!nav.ws.workspace}
          title={nav.ws.workspace ? "새 프로젝트 생성" : "먼저 워크스페이스를 선택하세요"}
          onClick={go(onCreateProject)}
        >
          ＋ 프로젝트 생성
        </button>
      </div>
      <div className="prail-bottom">
        <button className={`prail-item ${isShell("invitations") ? "on" : ""}`} onClick={go(() => nav.showShell("invitations"))}>
          내 초대{pendingInvites > 0 ? ` · ${pendingInvites}` : ""}
        </button>
        <button className={`prail-item ${isShell("myActivity") ? "on" : ""}`} onClick={go(() => nav.showShell("myActivity"))}>내 활동</button>
        <button className={`prail-item ${isShell("account") ? "on" : ""}`} onClick={go(() => nav.showShell("account"))}>내 계정</button>
        {admin && <button className={`prail-item ${isShell("admin") ? "on" : ""}`} onClick={go(() => nav.showShell("admin"))}>관리자</button>}
      </div>
      {/* Company logo, at the foot of the rail — the quiet end of the chrome rather than the top bar,
          which is now working space (홈·앱·다운로드 + the workspace switcher). */}
      <div className="prail-brand">
        <img className="prail-logo" src={logoUrl} alt="회사 로고" />
      </div>
    </nav>
  );
}

function ProjectNode({ p, nav, admin, onNavigated }: { p: Project; nav: ProjectNavController; admin: boolean; onNavigated: () => void }) {
  const expanded = nav.expandedProjectId === p.id;
  const active = nav.active;
  const isLeaf = (l: ProjectLeaf) =>
    active.kind === "project" && active.projectId === p.id && leafKey(active.leaf) === leafKey(l);

  const Leaf = ({ leaf, label, indent = 1 }: { leaf: ProjectLeaf; label: string; indent?: number }) => (
    <button
      className={`prail-leaf ${isLeaf(leaf) ? "on" : ""}`}
      style={{ paddingLeft: 12 + indent * 14 }}
      onClick={() => { nav.openLeaf(p.id, leaf, `${p.name} · ${label}`); onNavigated(); }}
    >
      {label}
    </button>
  );

  return (
    <div className="prail-project">
      <button className={`prail-proj-head ${expanded ? "open" : ""}`} onClick={() => nav.toggleProject(p.id)}>
        <span className="prail-caret">{expanded ? "▾" : "▸"}</span>
        <span className="prail-proj-name">{p.name}</span>
      </button>
      {expanded && (
        <div className="prail-leaves">
          {admin && <Leaf leaf={{ kind: "tab", tab: "overview" }} label="프로젝트 관리" />}

          {/* group 1: project-wide roll-up */}
          <Leaf leaf={{ kind: "eqms", section: "dashboard" }} label="대시보드" />
          <Leaf leaf={{ kind: "eqms", section: "documents" }} label="문서함" />
          {/* 자료함 has been routable all along but had no way in, so the project's uploaded files were
              unreachable from the UI. It belongs beside 문서함: both are places files live. */}
          <Leaf leaf={{ kind: "eqms", section: "attachments" }} label="자료함" />

          <div className="prail-leaf-sep" />

          {/* group 2: 스프린트 + 업무유형(work areas) — same level */}
          <Leaf leaf={{ kind: "eqms", section: "sprint" }} label="스프린트" />
          {WORK_AREA_LEAVES.map((w) => (
            <Leaf key={w.area} leaf={{ kind: "eqms", section: "workarea", area: w.area }} label={w.label} />
          ))}

          <div className="prail-leaf-sep" />

          {/* group 3: cross-cutting */}
          <Leaf leaf={{ kind: "eqms", section: "inbox" }} label="검토함" />
          <Leaf leaf={{ kind: "eqms", section: "external" }} label="외부문서" />
          {admin && <Leaf leaf={{ kind: "eqms", section: "admin" }} label="관리" />}

          <div className="prail-leaf-sep" />

          <Leaf leaf={{ kind: "tab", tab: "extapps" }} label="🧩 확장 앱" />
          <Leaf leaf={{ kind: "tab", tab: "blog" }} label="블로그" />
        </div>
      )}
    </div>
  );
}
